import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { eur } from "@/lib/apiClient";
import AdminLayout from "@/layouts/AdminLayout";
import { adminNav } from "@/lib/navConfig";
import { PageHeader } from "@/components/Common";
import { Input } from "@/components/ui/input";

const iso = (d) => d.toISOString().slice(0, 10);
const Tile = ({ label, value, tone = "text-slate-900", id }) => (
  <div className="bg-white border border-zinc-200 rounded-md p-4 min-w-0" data-testid={`fin-tile-${id}`}>
    <div className="text-xs uppercase tracking-wider text-zinc-500">{label}</div>
    <div className={`text-xl sm:text-2xl font-heading font-bold num mt-1 truncate ${tone}`}>{value}</div>
  </div>
);

const SiteTable = ({ rows, testid }) => (
  <div className="overflow-x-auto kqm-scroll"><table className="w-full text-sm" data-testid={testid}>
    <thead><tr className="text-left text-xs uppercase text-zinc-500 border-b"><th className="p-2">Site</th><th className="p-2 text-right">Revenue</th><th className="p-2 text-right">Profit</th><th className="p-2 text-right">Margin</th></tr></thead>
    <tbody>{rows.map((s) => (
      <tr key={s.id} className="border-b border-zinc-50"><td className="p-2"><div className="font-medium">{s.name}</div><div className="text-xs text-zinc-400">{s.customer_name}</div></td>
        <td className="p-2 text-right num">{eur(s.revenue)}</td><td className={`p-2 text-right num ${s.profit < 0 ? "text-rose-600" : "text-emerald-700"}`}>{eur(s.profit)}</td><td className="p-2 text-right num">{s.margin}%</td></tr>))}
      {rows.length === 0 && <tr><td colSpan={4} className="p-4 text-center text-zinc-400">No activity in this period.</td></tr>}
    </tbody></table></div>
);

export default function FinanceDashboard() {
  const now = new Date();
  const [start, setStart] = useState(iso(new Date(now.getFullYear(), 0, 1)));
  const [end, setEnd] = useState(iso(now));
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/finance/summary", { params: { start, end } }).then((r) => setD(r.data)); }, [start, end]);
  const t = d?.totals || {};
  return (
    <AdminLayout nav={adminNav} title="Finance">
      <PageHeader title="Finance" subtitle="Revenue, costs and profit across all sites">
        <div className="flex flex-wrap gap-2 items-center text-sm">
          <Input data-testid="fin-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-40" />
          <span className="text-zinc-400">to</span>
          <Input data-testid="fin-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-40" />
        </div>
      </PageHeader>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Tile id="revenue" label="Revenue" value={eur(t.revenue)} />
        <Tile id="cost" label="Total cost" value={eur(t.cost)} />
        <Tile id="profit" label="Gross profit" value={eur(t.profit)} tone={t.profit < 0 ? "text-rose-600" : "text-emerald-700"} />
        <Tile id="margin" label="Margin" value={`${t.margin ?? 0}%`} />
        <Tile id="labour" label="Labour cost" value={eur(t.labour_cost)} />
        <Tile id="materials" label="Materials cost" value={eur(t.materials_cost)} />
        <Tile id="supplier" label="Supplier costs" value={eur(t.supplier_costs)} />
        <Tile id="other" label="Other costs" value={eur(t.other_cost)} />
        <Tile id="overhead" label="Running costs" value={eur(t.overhead_cost)} />
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white border border-zinc-200 rounded-md p-4 min-w-0">
          <div className="flex justify-between items-center mb-2"><h3 className="font-heading font-bold">Top sites by profit</h3><Link to="/admin/site-profitability" className="text-xs text-amber-700" data-testid="fin-link-sites">Site profitability</Link></div>
          <SiteTable rows={d?.top_sites || []} testid="fin-top-sites" />
          {d?.worst_sites?.length > 0 && <><h3 className="font-heading font-bold mt-4 mb-2 text-rose-700">Loss-making sites</h3><SiteTable rows={d.worst_sites} testid="fin-worst-sites" /></>}
        </div>
        <div className="bg-white border border-zinc-200 rounded-md p-4 min-w-0">
          <div className="flex justify-between items-center mb-2"><h3 className="font-heading font-bold">Supplier spend</h3><Link to="/admin/materials" className="text-xs text-amber-700" data-testid="fin-link-materials">Materials and costs</Link></div>
          <div className="overflow-x-auto kqm-scroll"><table className="w-full text-sm" data-testid="fin-suppliers">
            <thead><tr className="text-left text-xs uppercase text-zinc-500 border-b"><th className="p-2">Supplier</th><th className="p-2 text-right">Entries</th><th className="p-2 text-right">Ex VAT</th><th className="p-2 text-right">Inc VAT</th></tr></thead>
            <tbody>{(d?.suppliers || []).map((s) => (
              <tr key={s.supplier} className="border-b border-zinc-50"><td className="p-2">{s.supplier}</td><td className="p-2 text-right num">{s.count}</td><td className="p-2 text-right num">{eur(s.ex_vat)}</td><td className="p-2 text-right num">{eur(s.inc_vat)}</td></tr>))}
              {d && d.suppliers.length === 0 && <tr><td colSpan={4} className="p-4 text-center text-zinc-400">No supplier spend in this period.</td></tr>}
            </tbody></table></div>
        </div>
      </div>
    </AdminLayout>
  );
}
